import { Dispatch, AnyAction } from 'redux';
import GenericGridCreator, { genericGridSuffix } from 'ps-components/AdvancedTable/GenericGrid/GenericGridCreator';
import { getGenericRow, getTransformedColumns } from 'ps-components/AdvancedTable/GenericGrid/GenericGridFunctions';
import apiClient from 'ps-components/lib/apiClient';
import { JobMatchDefaultColumns } from './JobMatchDefaultColumns';
import addAlert from 'ps-components/AlertToast/addAlert';
import { landingPageJobFacetSearch } from '../LandingPageFacet';
import { HIDDEN_TERMS } from '../../JobPricingDetails/JobPricingGrid/JobPricingGridCreate';
import { Job, HeaderData, JobMatchState } from './types/JobMatchDetailTypes';
import { SlottedMatchDetails } from './SlottedMatchDetails';
import { SurveyMatchDetails } from './SurveyMatchDetails';
const JOB_MATCHES_DETAILS_START = 'JOB_MATCHES_DETAILS_START';
const JOB_MATCHES_DETAILS_SUCCESS = 'JOB_MATCHES_DETAILS_SUCCESS';
const JOB_MATCHES_DETAILS_ERROR = 'JOB_MATCHES_DETAILS_ERROR';
const JOB_MATCHES_DETAILS_EXPANDED_TOGGLE = 'JOB_MATCHES_DETAILS_EXPANDED_TOGGLE';
const LANDING_PAGE_SORT = 'LANDING_PAGE' + genericGridSuffix.ORDER_BY;
const SEARCH_CLEAR_FACETS_FILTER = landingPageJobFacetSearch.SEARCH_CLEAR_FACETS_FILTER;
const SET_JOB_IS_STALE = 'SET_JOB_IS_STALE';
const SET_ALL_JOBS_STALE = 'SET_ALL_JOBS_STALE';
const UPDATE_DISPLAY_IN = 'UPDATE_DISPLAY_IN';

const initialState: JobMatchState = {
  jobMatchDetailsLoading: [],
  jobMatchExpandedSections: {},
  jobMatchTableProps: {},
  displayIn: '',
  defaultLeftColumns: [
    {
      Header: (data: HeaderData) => {
        return data.options.pricingType === 'slotted' ? 'Slotted Job Match' : 'Survey Job Match';
      },
      id: 'job-match-title',
      width: 200,
      accessor: null,
      accessTerms: true,
      customRender: (rowData, key, options) => options.pricingType === 'slotted'
        ? SlottedMatchDetails(rowData, key, options)
        : SurveyMatchDetails(rowData, key, options)
    },
    {
      Header: 'Aging',
      id: 'aging-column',
      width: 70,
      accessTerms: true,
      accessor: null,
      customRender: JobMatchDefaultColumns
    },
    {
      Header: 'Adj',
      id: 'adj-column',
      width: 70,
      accessTerms: true,
      accessor: null,
      customRender: JobMatchDefaultColumns
    },
    {
      Header: 'Geo',
      id: 'geo-column',
      width: 70,
      accessTerms: true,
      accessor: null,
      customRender: JobMatchDefaultColumns
    },
    {
      Header: 'Wgt',
      id: 'wgt-column',
      width: 70,
      accessTerms: true,
      accessor: null,
      customRender: JobMatchDefaultColumns
    }
  ],
  hiddenTerms: HIDDEN_TERMS,
};

const jobMatchGrid = new GenericGridCreator(
  'JOB_MATCHES',
  {},
  initialState,
  {
    getInstanceState: (store: any) => store.landingPage.jobMatchGridReducer
  }
);
export const jobMatchGridActions = jobMatchGrid.getActions();

jobMatchGridActions.getJobMatchDetails = (
  job: Job,
  benchmarkYear: number,
  displayIn: string,
  masterAccountId: string,
  currency: string,
  expanded = true,
  viewName: string | undefined,
  isSlotted = false
) => {
  return async (dispatch: Dispatch<any, any>) => {
    const errorMessage = `Error retrieving job match details for ${job.title}`;

    try {
      dispatch({ type: JOB_MATCHES_DETAILS_START, id: job.id });
      const options = {
        benchmarkYear,
        jobCode: job.code,
        jobCodeKey: job.codeKey,
        displayIn: displayIn.toLowerCase(),
        currency,
        viewName,
        isSlotted
      };
      let result;
      if (apiClient.apiPost) {
        result = await apiClient.apiPost(`/cms/api/accounts/${masterAccountId}/org-jobs/pricing`, options);
      }

      if (result?.status === 200) {
        // id is the job ID of the job being matched to
        dispatch({ type: JOB_MATCHES_DETAILS_SUCCESS, id: job.id, data: result, expanded });
      } else {
        throw Error(errorMessage);
      }
    } catch (error) {
      addAlert(errorMessage, 'danger');
      console.log(error);
      dispatch({ type: JOB_MATCHES_DETAILS_ERROR, id: job.id });
    }
  };
};

jobMatchGridActions.setIsStale = (id: string, isStale: boolean) =>
  id === 'all' ? ({ type: SET_ALL_JOBS_STALE, isStale }) : ({ type: SET_JOB_IS_STALE, id, isStale });

jobMatchGridActions.toggleJobMatchExpandedSection = (id: string, value: boolean) =>
  ({ type: JOB_MATCHES_DETAILS_EXPANDED_TOGGLE, id, value });

jobMatchGridActions.updateDisplayIn = (value: string) => ({ type: UPDATE_DISPLAY_IN, value });

function extendReducer(state: JobMatchState, action: AnyAction): JobMatchState | null {
  switch (action.type) {
    case JOB_MATCHES_DETAILS_START:
      const startJobMatchDetailsLoading = [ ...state.jobMatchDetailsLoading ];
      startJobMatchDetailsLoading.push(action.id);

      const startExpandedSections = { ...state.jobMatchExpandedSections };
      startExpandedSections[action.id] = true;

      return {
        ...state,
        jobMatchDetailsLoading: startJobMatchDetailsLoading,
        jobMatchExpandedSections: startExpandedSections
      };
    case JOB_MATCHES_DETAILS_SUCCESS:
      const successJobMatchDetailsLoading = state.jobMatchDetailsLoading.filter((jobId) => jobId !== action.id);
      const terms = action.data?.data?.data?.terms;
      const allData = action.data?.data?.data?.data;
      const termGroups = action.data?.data?.data?.termGroups;
      const pricingType = action.data?.data?.data?.metadata?.pricingType === 'slotted' ? 'slotted' : 'priced';

      const successExpandedSections = { ...state.jobMatchExpandedSections };
      if (successExpandedSections[action.id] && action.expanded === false) {
        successExpandedSections[action.id] = false;
      }

      const successJobMatchTableProps = { ...state.jobMatchTableProps };

      successJobMatchTableProps[action.id] = {
        ...state.jobMatchTableProps[action.id],
        columnHeaders: getTransformedColumns(
          termGroups,
          terms,
          allData,
          state.defaultLeftColumns,
          state.defaultRightColumns,
          state.termsColumnWidthOverride
        ),
        rowData: allData,
        summaryData: [],
        getDataRow: getGenericRow,
        dataLoading: false,
        totalCount: action.data?.data?.data?.metadata?.totalCount || action.data?.data?.data?.data?.length,
        noDataText: 'No Data',
        loadingText: 'Loading Grid...',
        hiddenColumns: [ ...state.hiddenTerms ],
        options: { pricingType, org_job_id: action.id, isJobMatch: true },
        isStale: false,
        className: state.tableProps?.className,
        rowClassName: state.tableProps?.rowClassName,
        bodyClassName: state.tableProps?.bodyClassName,
        headerClassName: state.tableProps?.headerClassName,
        headerRowClassName: state.tableProps?.headerRowClassName
      };

      return {
        ...state,
        jobMatchDetailsLoading: successJobMatchDetailsLoading,
        jobMatchExpandedSections: successExpandedSections,
        jobMatchTableProps: successJobMatchTableProps
      };
    case JOB_MATCHES_DETAILS_ERROR:
      const errorJobMatchDetailsLoading = state.jobMatchDetailsLoading.filter((jobId) => jobId !== action.id);

      const errorTableProps = { ...state.jobMatchTableProps };
      errorTableProps[action.id] = {
        ...state.jobMatchTableProps[action.id],
        rowData: []
      };

      return {
        ...state,
        jobMatchDetailsLoading: errorJobMatchDetailsLoading,
        jobMatchTableProps: errorTableProps
      };
    case JOB_MATCHES_DETAILS_EXPANDED_TOGGLE:
      const toggleExpandedSections = { ...state.jobMatchExpandedSections };
      toggleExpandedSections[action.id] = action.value;

      return {
        ...state,
        jobMatchExpandedSections: toggleExpandedSections
      };
    case SEARCH_CLEAR_FACETS_FILTER:
      return {
        ...state,
        jobMatchDetailsLoading: [],
        jobMatchExpandedSections: {},
        jobMatchTableProps: {}
      };
    case LANDING_PAGE_SORT:
      return { ...state, jobMatchExpandedSections: {} };
    case SET_JOB_IS_STALE:
      const { id, isStale } = action;
      if (!state.jobMatchTableProps[id]) return state;
      const staleTableProps = { ...state.jobMatchTableProps[id] };
      return {
        ...state,
        jobMatchTableProps: {
          ...state.jobMatchTableProps,
          [id]: { ...staleTableProps, isStale }
        }
      };
    case SET_ALL_JOBS_STALE:
      const allStaleTableProps = { ...state.jobMatchTableProps };
      Object.keys(state.jobMatchTableProps).forEach(jobId => (allStaleTableProps[jobId].isStale = action.isStale));
      return {
        ...state,
        jobMatchTableProps: allStaleTableProps
      };
    case UPDATE_DISPLAY_IN:
      return {
        ...state,
        displayIn: action.value
      };
    default:
      return null;
  }
}

export default jobMatchGrid.createReducer(extendReducer);
